// covered-by: frontends/carrie/src/components/ask-puntt-review/__tests__/useReviewArtworkFocus.test.ts
import { type AskPunttReviewComment } from "@mg/schemas/src/christo/askPunttReview";
import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { useCallback, useEffect, useRef, useState } from "react";

import { type AssetViewerPaneHandle } from "../../routes/ask-puntt/components/AssetViewerPane";
import { isPositivePageNum } from "../../routes/ask-puntt/utils/pageNum";

type ReviewComment = BaseTicketComment | AskPunttReviewComment;

export function useReviewArtworkFocus(options: {
  comments: readonly ReviewComment[] | undefined;
  initialCommentId?: string | null;
  onCommentDeepLinkChange?: (commentId: string | null) => void;
}) {
  const { comments, initialCommentId, onCommentDeepLinkChange } = options;
  const artworkPaneRef = useRef<AssetViewerPaneHandle>(null);
  const hydratedCommentRef = useRef<string | null>(null);

  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    initialCommentId ?? null,
  );
  const [artworkHeaderControls, setArtworkHeaderControls] =
    useState<React.ReactNode | null>(null);

  const focusComment = useCallback((comment: ReviewComment) => {
    if (isPositivePageNum(comment.page_num)) {
      artworkPaneRef.current?.navigateToPage(comment.page_num);
    }
  }, []);

  useEffect(() => {
    if (!initialCommentId || !comments?.length) return;
    if (hydratedCommentRef.current === initialCommentId) return;
    const comment = comments.find((c) => c._id === initialCommentId);
    if (!comment) return;
    hydratedCommentRef.current = initialCommentId;
    setSelectedCommentId(initialCommentId);
    focusComment(comment);
  }, [initialCommentId, comments, focusComment]);

  useEffect(() => {
    if (!comments?.length) {
      setSelectedCommentId(null);
      hydratedCommentRef.current = null;
      return;
    }
    setSelectedCommentId((current) => {
      if (current == null) return current;
      if (comments.some((comment) => comment._id === current)) return current;
      hydratedCommentRef.current = null;
      return null;
    });
  }, [comments]);

  const selectComment = useCallback(
    (commentId: string) => {
      const comment = comments?.find((c) => c._id === commentId);
      if (!comment) return;
      setSelectedCommentId(commentId);
      onCommentDeepLinkChange?.(commentId);
      focusComment(comment);
    },
    [comments, focusComment, onCommentDeepLinkChange],
  );

  const clearComment = useCallback(() => {
    setSelectedCommentId(null);
    onCommentDeepLinkChange?.(null);
  }, [onCommentDeepLinkChange]);

  const toggleComment = useCallback(
    (commentId: string) => {
      if (selectedCommentId === commentId) {
        clearComment();
        return;
      }
      selectComment(commentId);
    },
    [clearComment, selectComment, selectedCommentId],
  );

  return {
    artworkPaneRef,
    selectedCommentId,
    artworkHeaderControls,
    setArtworkHeaderControls,
    selectComment,
    clearComment,
    toggleComment,
  };
}
